









































































































































































































import { IQuote } from '@/models';
import { PropType } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { InputValidationRule } from 'vuetify';

import {
  IQuoteApplyDiscountPercentagesPayload,
  IQuoteCalculateCoreDiscountPercentPayload
} from '@/store/quote';
import { ALForm } from '@/common/component.types';
import { ErrorManager } from '@/models/error';

import mixins from '@/utils/mixins';
import auth from '@/mixins/auth';
import snackbar from '@/mixins/snackbar';
import functions from '@/mixins/functions';

import ErrorAlert from '../common/ErrorAlert.vue';
import Messages from '../common/Messages.vue';
import { roundCurrency } from '@/utils/helpers';

const baseMixins = mixins(auth, functions, snackbar);
export interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    form: ALForm;
  };
}

export default baseMixins
  .extend<Options>({
    functional: false
  })
  .extend({
    name: 'QuoteReviewingDiscountsCard',

    components: { ErrorAlert, Messages },

    props: {
      quote: Object as PropType<IQuote>,
      dirty: Boolean as PropType<boolean>
    },

    data() {
      return {
        // Data
        commissionPercent: 0,
        ewgDiscountPercent: 0,
        targetDiscountPercent: 0,
        specialDiscountPercent: 0,
        effectiveAccessoryDiscountPercent: 0,
        effectiveCoreDiscountPercent: 0,

        isDiscountFormValid: false,
        errorMessage: '',
        detailErrorMessage: '',
        isCalculatingCoreDiscountPercentage: false,
        isApplyingDiscounts: false,

        calculatedEffectiveCoreDiscountPercent: null as null | number,
        isEffectiveCoreDiscountPercentMatching: true
      };
    },
    watch: {
      quote: {
        handler(value: IQuote) {
          this.ewgDiscountPercent = value.ewg_discount_percent ?? 0;
          this.targetDiscountPercent = value.target_discount_percent ?? 0;
          this.specialDiscountPercent = value.special_discount_percent ?? 0;
          this.effectiveAccessoryDiscountPercent =
            value.effective_accessory_discount_percentage ?? 0;
          this.effectiveCoreDiscountPercent =
            value.effective_core_discount_percentage ?? 0;

          this.calculateGlobalDiscount(this.effectiveCoreDiscountPercent);

          this.isDirty = false;
        },
        immediate: true,
        deep: true
      }
    },

    computed: {
      ...mapGetters({
        getCustomerById: 'customer/getCustomerById'
      }),

      isDirty: {
        get(): boolean {
          return this.dirty;
        },
        set(dirty: boolean) {
          this.$emit('update:dirty', dirty);
        }
      },

      percentRules(): InputValidationRule[] {
        return [
          (v: number) => v <= 100 || 'Cannot be greater than 100%',
          (v: number) => v >= 0 || 'Cannot be less than 0%'
        ];
      },

      isQuoteCustomerEWG(): boolean {
        if (this.quote.customer?.identifier) {
          return (
            this.getCustomerById(this.quote.customer.identifier)?.type === 'EWG'
          );
        }

        return false;
      }
    },

    methods: {
      ...mapActions({
        calculateCoreDiscountPercent: 'quote/calculateCoreDiscountPercent',
        applyDiscountPercentages: 'quote/applyDiscountPercentages'
      }),

      clearAllErrors() {
        this.errorMessage = '';
        this.detailErrorMessage = '';
      },

      validateForm(): boolean {
        let valid = false;
        if (this.$refs.form) {
          valid = this.$refs.form.validate();
        }
        return valid;
      },

      updateDiscountInputField(discount?: number) {
        this.isDirty = true;
        this.debounceCalculateGlobalDiscount(discount);
      },

      debounceCalculateGlobalDiscount(discount?: number) {
        this.isCalculatingCoreDiscountPercentage = true;
        return this.debounce(this.calculateGlobalDiscount, 1000)(discount);
      },

      matchCoreDiscountToCalculated() {
        this.effectiveCoreDiscountPercent = this
          .calculatedEffectiveCoreDiscountPercent as number;
        this.isEffectiveCoreDiscountPercentMatching = true;

        // if the discounts are updated then the form is dirty
        this.isDirty = true;
      },

      calculateGlobalDiscount(discount?: number) {
        this.clearAllErrors();

        this.isCalculatingCoreDiscountPercentage = true;

        const payload: IQuoteCalculateCoreDiscountPercentPayload = {
          id: this.quote.number!,
          data: {
            target_discount_percent: Number(this.targetDiscountPercent),
            accessory_discount_percent: Number(
              this.effectiveAccessoryDiscountPercent
            )
          }
        };

        this.calculateCoreDiscountPercent(payload)
          .then(calculatedCoreDiscountPercentage => {
            // if no discount provided then use the current core percentage to compare;
            const newEffectiveDiscount =
              discount ?? this.effectiveCoreDiscountPercent;

            this.isEffectiveCoreDiscountPercentMatching =
              newEffectiveDiscount == calculatedCoreDiscountPercentage;

            this.calculatedEffectiveCoreDiscountPercent = calculatedCoreDiscountPercentage;

            this.$emit(
              'update:validity',
              this.isEffectiveCoreDiscountPercentMatching
            );
          })
          .catch(error => {
            if (error.response) {
              if (error.response.data.detail) {
                this.detailErrorMessage = error.response.data.detail;
              }
            }
            this.errorMessage = ErrorManager.extractApiError(error);
          })
          .finally(() => {
            this.isCalculatingCoreDiscountPercentage = false;
          });
      },

      applyDiscounts() {
        // validate each field to determine errors where necessary
        this.validateForm();
        this.clearAllErrors();

        this.isApplyingDiscounts = true;

        const payload: IQuoteApplyDiscountPercentagesPayload = {
          id: this.quote.number!,
          data: {
            target_discount_percentage: Number(this.targetDiscountPercent),
            core_discount_percentage: Number(this.effectiveCoreDiscountPercent),
            accessory_discount_percentage: Number(
              this.effectiveAccessoryDiscountPercent
            ),
            ewg_discount_percentage: Number(this.ewgDiscountPercent),
            special_discount_percentage: Number(this.specialDiscountPercent)
          }
        };

        this.applyDiscountPercentages(payload)
          .then(() => {
            this.createSnack({
              color: 'success darken-1',
              type: 'success',
              right: true,
              useBtn: false,
              msg: `Applied discounts successfully`,
              btnText: 'Ok',
              timeout: 6000
            });
            this.$emit('updated:quote');

            this.isDirty = false;
          })
          .catch(error => {
            if (error.response) {
              if (error.response.data.detail) {
                this.detailErrorMessage = error.response.data.detail;
              }
            }
            this.errorMessage = ErrorManager.extractApiError(error);
          })
          .finally(() => {
            this.isApplyingDiscounts = false;
          });
      },

      /**
       * This is used in multiple places but the function's name in invalid for its current use case
       * TODO: LH - 2024-03-07 - Refactor function name to be more generic
       */
      roundedPercentage: roundCurrency
    }
  });
